import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'sonner'

import type {
    ConfirmOrderPayload,
    CreateOrderRequest,
    ListOrdersParams,
    ListStockOutNotesParams,
    PaymentMethodChangeTarget,
    QuickSaleRequest,
    RecordPaymentInput,
    SalesOrder,
    StockOutItemInput,
    StockOutMaterialItemInput,
    UpdateOrderRequest,
} from '@/lib/api/sales-order-api'

import type { RefundChoice } from '@/lib/api/sales-return-api'
import { m } from '@/lib/paraglide/messages'
import { salesOrderApi, stockOutNoteApi } from '@/lib/api/sales-order-api'
import { tripKeys } from '@/hooks/use-delivery-trips-query'
import { expenseReportKeys } from '@/hooks/use-expense-report-query'
import { vehicleKeys } from '@/hooks/use-vehicles-query'
import { useAuth } from '@/hooks/use-auth'
import { ApiError } from '@/lib/api/client'
import { formatApiError } from '@/lib/format-api-error'
import { orderDeliveryPollInterval, orderPollInterval } from '@/lib/order-poll'

// Pipe an error through formatApiError so the toast carries the server's
// real reason + the X-Request-ID trace tag — turns a generic "thất bại"
// into something a user can quote and a dev can grep.
const errorToast = (fallback: string) => (err: unknown) => {
    const payload = formatApiError(err, fallback)
    toast.error(payload.title, { description: payload.description })
}

export const orderKeys = {
    all: ['sales-orders'] as const,
    lists: () => [...orderKeys.all, 'list'] as const,
    list: (params: ListOrdersParams) => [...orderKeys.lists(), params] as const,
    detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
    timeline: (id: string) => [...orderKeys.all, 'timeline', id] as const,
    payments: (id: string) => [...orderKeys.all, 'payments', id] as const,
    paymentQR: (id: string) => [...orderKeys.all, id, 'payment-qr'] as const,
    packagingDraft: (id: string) =>
        [...orderKeys.all, 'packaging-draft', id] as const,
    packagingDeducted: (id: string) =>
        [...orderKeys.all, 'packaging-deducted', id] as const,
    lotDebtOverview: () => [...orderKeys.all, 'lot-debt-overview'] as const,
    inventoryDeducted: (id: string) =>
        [...orderKeys.all, 'inventory-deducted', id] as const,
    stockCheck: (id: string) => [...orderKeys.all, 'stock-check', id] as const,
    stockOutSuggestion: (id: string) =>
        [...orderKeys.all, 'stock-out-suggestion', id] as const,
    assignableDrivers: () => [...orderKeys.all, 'assignable-drivers'] as const,
    returnable: (id: string) => [...orderKeys.all, 'returnable', id] as const,
    returns: (id: string) => [...orderKeys.all, 'returns', id] as const,
    returnPreview: (id: string, items: unknown) =>
        [...orderKeys.all, 'return-preview', id, items] as const,
}

/**
 * Tài xế cho popup gán đơn. Chỉ fetch khi popup mở (`enabled`) để không gọi
 * endpoint cần quyền `sales_orders.assign_driver` với mọi lượt xem đơn.
 */
export function useAssignableDrivers(enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.assignableDrivers(),
        queryFn: async () => {
            const res = await salesOrderApi.assignableDrivers()
            return res.data ?? []
        },
        enabled,
    })
}

export function useSalesOrders(
    params?: ListOrdersParams,
    options: {
        enabled?: boolean
        /** Tự tải lại sau mỗi khoảng ms (chỉ khi tab đang mở). */
        refetchInterval?: number | false
    } = {},
) {
    return useQuery({
        enabled: options.enabled,
        refetchInterval: options.refetchInterval,
        queryKey: orderKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await salesOrderApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

/**
 * Danh sách đơn tải theo trang (cuộn / "Xem thêm" gọi fetchNextPage) — màn
 * "Đơn chờ nhận" dùng thay cho việc tải hết mọi trang một lượt (FD-396: 16
 * request nối đuôi cho ~1.600 đơn trên prod). Giữ dữ liệu cũ khi đổi bộ lọc để
 * ô tìm kiếm không nháy trạng thái "đang tải".
 */
export function useInfiniteSalesOrders(
    params: Omit<ListOrdersParams, 'page' | 'per_page'>,
    perPage: number,
) {
    return useInfiniteQuery({
        queryKey: [...orderKeys.lists(), 'infinite', params, perPage] as const,
        queryFn: async ({ pageParam }): Promise<InfiniteOrdersPage> => {
            const res = await salesOrderApi.list({
                ...params,
                page: pageParam,
                per_page: perPage,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.meta && lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined,
        placeholderData: keepPreviousData,
    })
}

const INFINITE_ORDER_PAGE_SIZE = 20

interface InfiniteOrdersPage {
    items: SalesOrder[]
    meta:
        | { page: number; per_page: number; total: number; total_pages: number }
        | undefined
}

/**
 * Ô chọn đơn có debounce + cuộn tải thêm (màn đối soát). Chỉ lấy đơn còn nhận
 * thanh toán (payable=true: chưa huỷ + còn phải thu > 0). Dùng chung API danh
 * sách đơn, search theo mã đơn / mã nội bộ / tên khách / SĐT; đơn khớp đúng mã
 * lên trên, rồi mới nhất trước.
 */
export function useInfiniteSalesOrdersQuery(search: string) {
    return useInfiniteQuery({
        queryKey: [...orderKeys.lists(), 'infinite-payable', search] as const,
        queryFn: async ({ pageParam = 1 }): Promise<InfiniteOrdersPage> => {
            const res = await salesOrderApi.list({
                page: pageParam,
                per_page: INFINITE_ORDER_PAGE_SIZE,
                search: search || undefined,
                payable: true,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.meta && lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined,
    })
}

export function useSalesOrder(
    id: string,
    opts?: { pollWhileUnpaid?: boolean; pollUntilDelivered?: boolean },
) {
    return useQuery({
        queryKey: orderKeys.detail(id),
        queryFn: async () => {
            const res = await salesOrderApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
        // While a bank-transfer invoice popup waits for the SePay webhook, poll
        // the order every 4s until it flips to paid (no SSE on /sale screens yet).
        // Popup POS chờ thủ kho xuất kho (M-76) poll tới khi đơn delivered.
        refetchInterval: (query) =>
            orderPollInterval(
                opts?.pollWhileUnpaid ?? false,
                query.state.data?.payment_status,
            ) ||
            orderDeliveryPollInterval(
                opts?.pollUntilDelivered ?? false,
                query.state.data?.status,
            ),
    })
}

export function useOrderTimeline(id: string) {
    return useQuery({
        queryKey: orderKeys.timeline(id),
        queryFn: async () => {
            const res = await salesOrderApi.timeline(id)
            return res.data ?? []
        },
        enabled: Boolean(id),
    })
}

// Phase 4 — đọc draft packaging của đơn (sale chốt lúc tạo). FE Confirm flow
// dùng để hiển thị + override packaging_items.
export function useOrderPackagingDraft(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.packagingDraft(id),
        queryFn: async () => {
            const res = await salesOrderApi.getPackagingDraft(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0, // mỗi lần Confirm cần fresh
    })
}

// Vật tư đã trừ ở Confirm step (group theo lot). Dùng cho:
//  - ConfirmReturn popup prefill list.
//  - Trang chi tiết đơn hiển thị "Vật tư đã trừ".
export function useOrderPackagingDeducted(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.packagingDeducted(id),
        queryFn: async () => {
            const res = await salesOrderApi.getPackagingDeducted(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0,
    })
}

// SP đã trừ kho ở Confirm step (group theo lot). Mirror packagingDeducted —
// dùng cho ConfirmReturn popup prefill phần SP.
export function useOrderInventoryDeducted(id: string, enabled = true) {
    return useQuery({
        queryKey: orderKeys.inventoryDeducted(id),
        queryFn: async () => {
            const res = await salesOrderApi.getInventoryDeducted(id)
            return res.data ?? []
        },
        enabled: Boolean(id) && enabled,
        staleTime: 0,
    })
}

/**
 * Tồn đọng nợ lô toàn hệ thống cho ô thống kê ở dashboard kho (FD-372).
 *
 * Làm mới mỗi phút: nợ phát sinh khi quầy bán và đóng khi kho duyệt phiếu nhập —
 * cả hai đều xảy ra ở màn khác, nên số trên dashboard phải tự đuổi kịp thay vì
 * bắt quản lý F5.
 */
export function useLotDebtOverview(enabled = true) {
    return useQuery({
        queryKey: orderKeys.lotDebtOverview(),
        queryFn: async () => {
            const res = await salesOrderApi.getLotDebtOverview()
            return res.data
        },
        enabled,
        refetchInterval: 60_000,
    })
}

export function useOrderPayments(id: string) {
    return useQuery({
        queryKey: orderKeys.payments(id),
        queryFn: async () => {
            const res = await salesOrderApi.listPayments(id)
            return res.data ?? []
        },
        enabled: Boolean(id),
    })
}

export function usePaymentQR(orderId: string, enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.paymentQR(orderId),
        queryFn: async () => {
            const res = await salesOrderApi.getPaymentQR(orderId)
            return res.data ?? null
        },
        enabled: enabled && Boolean(orderId),
        refetchInterval: (query) => {
            // Stop polling once payment confirmed (amount === 0 means fully paid)
            const data = query.state.data
            if (data && data.amount <= 0) return false
            return 3000
        },
    })
}

// useNotifications moved to use-notifications.ts (single source of truth)

/**
 * Kiểm tra tồn kho đủ để Confirm cho đơn AwaitingPackaging.
 * Enable=false khi order không ở trạng thái awaiting_packaging (BE sẽ 400).
 * FE poll nhẹ để cashier F5 thấy kho đủ mà bấm Confirm.
 */
export function useStockCheck(orderId: string, enabled: boolean) {
    return useQuery({
        queryKey: orderKeys.stockCheck(orderId),
        queryFn: async () => {
            const res = await salesOrderApi.checkStock(orderId)
            return res.data ?? { ready: false, shortfalls: [] }
        },
        enabled: enabled && Boolean(orderId),
        // Không auto-poll để tránh spam BE; cashier bấm "Kiểm tra đủ hàng"
        // để refetch tay khi cần.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
    })
}

export function useCreateOrder(options?: {
    // Return true để bỏ qua default toast (caller sẽ hiển thị UI riêng, vd
    // popup 409 INSUFFICIENT_STOCK cho luồng AwaitingPackaging).
    onErrorHandled?: (err: unknown) => boolean
}) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateOrderRequest) => salesOrderApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_create_success())
        },
        onError: (err) => {
            const handled = options?.onErrorHandled?.(err) ?? false
            if (!handled) {
                errorToast(m.toast_order_create_error())(err)
            }
        },
    })
}

export function useUpdateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateOrderRequest }) =>
            salesOrderApi.update(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_update_success())
        },
        onError: errorToast(m.toast_order_update_error()),
    })
}

export function useUpdateInternalOrderCode() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, code }: { id: string; code: string }) =>
            salesOrderApi.updateInternalCode(id, code),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_internal_code_success())
        },
        onError: errorToast(m.toast_order_update_error()),
    })
}

export function useQuickCreateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: QuickSaleRequest) => salesOrderApi.quickCreate(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_quick_success())
        },
        onError: errorToast(m.toast_order_quick_error()),
    })
}

function useNoteAction(
    apiFn: (id: string, data: { note: string }) => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { note: string } }) =>
            apiFn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(successMsg)
        },
        onError: errorToast(errorMsg),
    })
}

export function useConfirmOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data?: ConfirmOrderPayload
        }) => salesOrderApi.confirm(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            // Lot balances changed → drop any cached available-lots list.
            void qc.invalidateQueries({ queryKey: ['inventory-lots'] })
            toast.success(m.toast_order_confirm_success())
        },
        onError: errorToast(m.toast_order_confirm_error()),
    })
}

export function useStockOutOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            items,
            materialItems,
        }: {
            id: string
            items?: StockOutItemInput[]
            materialItems?: StockOutMaterialItemInput[]
        }) =>
            salesOrderApi.stockOut(
                id,
                items || materialItems
                    ? { items, material_items: materialItems }
                    : undefined,
            ),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            // Tồn lô đã đổi → drop cache lô + linked PXK.
            void qc.invalidateQueries({ queryKey: ['inventory-lots'] })
            void qc.invalidateQueries({ queryKey: ['product-lots'] })
            toast.success(m.toast_order_stock_out_success())
        },
        onError: errorToast(m.toast_order_stock_out_error()),
    })
}

/** Gợi ý lô cho popup "Tạo phiếu xuất kho" — lazy, chỉ fetch khi popup mở. */
export function useStockOutSuggestion(orderId: string, enabled: boolean) {
    return useQuery({
        enabled: enabled && Boolean(orderId),
        queryKey: orderKeys.stockOutSuggestion(orderId),
        queryFn: async () => {
            const res = await salesOrderApi.getStockOutSuggestion(orderId)
            return res.data ?? null
        },
    })
}

export function useStartDelivery() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, vehicle_id }: { id: string; vehicle_id?: string }) =>
            salesOrderApi.startDelivery(id, { vehicle_id }),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            toast.success(m.toast_order_start_delivery_success())
        },
        onError: (err) => {
            const code =
                err instanceof AxiosError
                    ? err.response?.data?.error?.code
                    : undefined
            if (code === 'VEHICLE_NOT_AVAILABLE') {
                toast.error(m.trip_error_vehicle_not_available())
            } else if (code === 'VEHICLE_REQUIRED') {
                toast.error(m.trip_error_vehicle_required())
            } else if (code === 'TRIP_VEHICLE_MISMATCH') {
                toast.error(m.trip_error_vehicle_mismatch())
            } else {
                errorToast(m.toast_order_start_delivery_error())(err)
            }
        },
    })
}

export function useConfirmDelivery() {
    const qc = useQueryClient()
    return useMutation({
        // Forward the FULL settlement payload — dropping method/cash_amount
        // here silently turns a driver cash collection into "record nothing".
        mutationFn: ({
            id,
            ...data
        }: { id: string } & Parameters<
            typeof salesOrderApi.confirmDelivery
        >[1]) => salesOrderApi.confirmDelivery(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(m.toast_order_confirm_delivery_success())
        },
        onError: errorToast(m.toast_order_confirm_delivery_error()),
    })
}

export function useConfirmReturn() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data?: {
                note?: string
                packaging_restore_items?: {
                    packaging_lot_id: string
                    quantity: number
                }[]
                /** FD-397: SL hàng lỗi theo lô — phần lỗi vào kệ hàng lỗi. */
                defect_items?: {
                    product_lot_id: string
                    quantity: number
                }[]
                /** M-38: kệ nhập cho phần hàng tốt theo lô. */
                restock_positions?: {
                    product_lot_id: string
                    position_id: string
                    new_lot?: boolean
                }[]
                /** M-38: kệ nhập cho phần hàng lỗi theo lô. */
                defect_positions?: {
                    product_lot_id: string
                    position_id: string
                    new_lot?: boolean
                }[]
                /** M-44: lựa chọn hoàn tiền khách đã trả. */
                refund?: RefundChoice
            }
        }) => salesOrderApi.confirmReturn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            void qc.invalidateQueries({
                queryKey: orderKeys.packagingDeducted(id),
            })
            toast.success(m.toast_order_return_success())
        },
        onError: errorToast(m.toast_order_return_error()),
    })
}

export function useAssignDriver() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: { driver_id: string }
        }) => salesOrderApi.assignDriver(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_assign_driver_success())
        },
        onError: errorToast(m.toast_order_assign_driver_error()),
    })
}

export function usePickupOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data?: { note?: string } }) =>
            salesOrderApi.pickupOrder(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(m.toast_order_pickup_success())
        },
        onError: errorToast(m.toast_order_pickup_error()),
    })
}

export function useFailDelivery() {
    return useNoteAction(
        (id, data) => salesOrderApi.failDelivery(id, data),
        m.toast_order_fail_delivery_success(),
        m.toast_order_fail_delivery_error(),
    )
}

// Carrier reconcile hooks — kế toán mark trạng thái cho đơn của carrier
// ngoài (Viettelpost, GHTK, …). Note optional. Cùng invalidate + toast
// shape với các delivery hook khác.
function useCarrierMarkAction(
    apiFn: (id: string, data?: { note?: string }) => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data?: { note?: string } }) =>
            apiFn(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(successMsg)
        },
        onError: errorToast(errorMsg),
    })
}

export function useCarrierMarkReadyToDeliver() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkReadyToDeliver(id, data),
        m.toast_order_carrier_mark_ready_to_deliver_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkInDelivery() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkInDelivery(id, data),
        m.toast_order_carrier_mark_in_delivery_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkDelivered() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkDelivered(id, data),
        m.toast_order_carrier_mark_delivered_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCarrierMarkDeliveryFailed() {
    return useCarrierMarkAction(
        (id, data) => salesOrderApi.carrierMarkDeliveryFailed(id, data),
        m.toast_order_carrier_mark_delivery_failed_success(),
        m.toast_order_carrier_mark_error(),
    )
}

export function useCancelOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: { note: string; refund?: RefundChoice }
        }) => salesOrderApi.cancel(id, data),
        onSuccess: (_result, { id }) => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(id) })
            toast.success(m.toast_order_cancel_success())
        },
        onError: errorToast(m.toast_order_cancel_error()),
    })
}

/** Huỷ đơn đã xác nhận rồi tạo đơn nháp mới sao chép nó. Trả về ĐƠN MỚI để
 *  caller điều hướng sang màn sửa của đơn đó. */
export function useRecreateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (orderId: string) => salesOrderApi.recreate(orderId),
        onSuccess: (_res, orderId) => {
            // Đơn cũ vừa chuyển sang "đã huỷ" và danh sách có thêm đơn nháp mới.
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_order_recreate_success())
        },
        onError: errorToast(m.toast_order_recreate_error()),
    })
}

/** Sao chép đơn có sẵn thành đơn nháp mới (M-187). Đơn gốc không đổi nên chỉ
 *  làm mới danh sách. Trả về ĐƠN MỚI để caller mở đơn đó. */
export function useDuplicateOrder() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (orderId: string) => salesOrderApi.duplicate(orderId),
        onSuccess: (res) => {
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(
                m.toast_order_duplicate_success({
                    code: res.data?.order_code ?? '',
                }),
            )
        },
        onError: errorToast(m.toast_order_duplicate_error()),
    })
}

export function useRecordOrderPayments(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (payments: RecordPaymentInput[]) =>
            salesOrderApi.recordPayments(orderId, { payments }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.payments(orderId) })
            toast.success(m.toast_order_payment_success())
        },
        onError: (err) => {
            errorToast(m.toast_order_payment_error())(err)
            // The order changed under a stale screen (SePay webhook or another
            // cashier collected first): refetch so the remaining amount is real.
            if (err instanceof ApiError && err.code === 'OVERPAYMENT') {
                void qc.invalidateQueries({
                    queryKey: orderKeys.detail(orderId),
                })
            }
        },
    })
}

/**
 * Sau khi đổi hình thức (M-152): lịch sử thanh toán của MỌI đơn (đổi phiếu thu
 * đổi luôn khoản phân bổ ở đơn khác) và các báo cáo chia theo hình thức (Thu
 * chi, Dòng tiền, Bán hàng, Báo cáo chi phí).
 */
export function invalidateAfterMethodChange(
    qc: ReturnType<typeof useQueryClient>,
) {
    void qc.invalidateQueries({ queryKey: [...orderKeys.all, 'payments'] })
    void qc.invalidateQueries({ queryKey: ['reports'] })
    void qc.invalidateQueries({ queryKey: expenseReportKeys.all })
}

/**
 * Đổi một khoản thu của đơn giữa Tiền mặt và Chuyển khoản (M-152). Số tiền và
 * trạng thái đơn không đổi; các báo cáo chia theo hình thức (Thu chi, Dòng
 * tiền, Bán hàng, Báo cáo chi phí) phải tải lại.
 */
export function useChangeOrderPaymentMethod(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            paymentId,
            method,
        }: {
            paymentId: string
            method: PaymentMethodChangeTarget
        }) => salesOrderApi.changePaymentMethod(orderId, paymentId, method),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            invalidateAfterMethodChange(qc)
            // Khoản phiếu thu / phiếu dư đổi theo → sổ công nợ khách
            // (= customerDebtKeys.all; import trực tiếp sẽ thành vòng import
            // vì use-customer-debt-query dùng invalidateAfterMethodChange).
            void qc.invalidateQueries({ queryKey: ['customer-debt'] })
            toast.success(m.order_payment_method_change_success())
        },
        onError: (err) => {
            errorToast(m.order_payment_method_change_error())(err)
            // Khoản vừa bị người khác đổi / xoá: tải lại để thấy trạng thái thật.
            void qc.invalidateQueries({ queryKey: orderKeys.payments(orderId) })
        },
    })
}

export const stockOutNoteKeys = {
    all: ['stock-out-notes'] as const,
    byCode: (code: string) =>
        [...stockOutNoteKeys.all, 'by-code', code] as const,
    byOrder: (orderId: string) =>
        [...stockOutNoteKeys.all, 'by-order', orderId] as const,
    list: (params: ListStockOutNotesParams) =>
        [...stockOutNoteKeys.all, 'list', params] as const,
}

export function useStockOutNote(code: string) {
    return useQuery({
        queryKey: stockOutNoteKeys.byCode(code),
        queryFn: async () => {
            const res = await stockOutNoteApi.getByCode(code)
            return res.data ?? null
        },
        enabled: Boolean(code),
        retry: false,
    })
}

/** Fetch a paginated list of stock-out notes with optional status filter. */
export function useStockOutNotes(params?: ListStockOutNotesParams) {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    return useQuery({
        queryKey: stockOutNoteKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await stockOutNoteApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
        enabled: arePermissionsLoaded && hasPermission('stock_out.confirm'),
    })
}

export function useConfirmStockOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            note,
            photoPaths,
        }: {
            id: string
            note?: string
            photoPaths: string[]
        }) =>
            stockOutNoteApi.confirm(id, {
                ...(note ? { note } : {}),
                photo_paths: photoPaths,
            }),
        onSuccess: (res) => {
            void qc.invalidateQueries({ queryKey: stockOutNoteKeys.all })
            const orderId = res.data?.sales_order_id
            if (orderId) {
                void qc.invalidateQueries({
                    queryKey: orderKeys.detail(orderId),
                })
                void qc.invalidateQueries({
                    queryKey: orderKeys.timeline(orderId),
                })
            }
            void qc.invalidateQueries({ queryKey: orderKeys.lists() })
            toast.success(m.toast_stock_out_note_confirm_success())
        },
        onError: errorToast(m.toast_stock_out_note_confirm_error()),
    })
}
